import { Root, router } from '@/app/addRouter.tsx';
import { ThemeProvider } from '@/components/ThemeProvider';
import { Toaster } from '@/components/ui/sonner';
import { clearAuthLog, readAuthLog } from '@/config/authLogBuffer';
import { configureLogging, logger } from '@/config/logging.ts';
import { DemoApp } from '@/demos/DemoApp.tsx';
import { HolidayProvider } from '@/features/holidays/HolidayProvider';
import { retiredOriginTarget } from '@/config/prevOrigin';
import {
  getSyncStatus,
  setSwPassthrough,
} from '@/features/offline/swBridge.ts';
import { queryClient, setQueryCacheDisabled } from '@/lib/query-client';
import AuthService from '@/services/AuthService.ts';
import { registerServiceWorker } from '@/services/swRegistration.ts';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { IntlProvider } from 'react-intl';
import './index.css';

// Initialize app with logging
async function initializeApp() {
  // Configure logging before anything else
  await configureLogging();

  // next.certhon.app is retired but stays an alias, because a cached worker
  // shell never sees nginx's 301. Move to the same page on certhon.app,
  // unless offline changes still wait for upload here: then stay this load,
  // the worker syncs, and the next load moves.
  const retiredTarget = retiredOriginTarget(window.location);
  if (retiredTarget) {
    const status = await getSyncStatus();
    // #3576: a tab restored on the retired host lands on an origin with its
    // own (possibly empty) storage — a re-login that looks like "Chrome
    // closed". Logged on this origin's auth buffer before leaving.
    logger.getChild(['service', 'auth']).warn('Retired hostname, moving', {
      from: window.location.href,
      to: retiredTarget,
      pendingUploads: status?.pendingUploads ?? null,
      pendingEvents: status?.pendingEvents ?? null,
      hasSession: !!localStorage.getItem('auth_service_state'),
    });
    if (!status || status.pendingUploads + status.pendingEvents === 0) {
      window.location.replace(retiredTarget);
      return;
    }
  }

  registerServiceWorker();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).serv = {
    auth: AuthService.getInstance(),
    // #3576: persisted auth log (survives reloads and re-logins).
    // serv.authLog() prints it, serv.authLog.clear() empties it.
    authLog: Object.assign(
      () => {
        const entries = readAuthLog();
        console.table(
          entries.map((e) => ({
            t: e.t,
            level: e.level,
            category: e.category.replace(/^frontend\./, ''),
            message: e.message,
          })),
        );
        return entries;
      },
      { clear: clearAuthLog, raw: readAuthLog },
    ),
    // Dev tool for offline-SW testing: serv.queryCache.disable() makes every
    // mount refetch through the service worker (see lib/query-client.ts).
    queryCache: {
      disable: () => setQueryCacheDisabled(true),
      enable: () => setQueryCacheDisabled(false),
    },
    // serv.sw.bypass() routes every request past the SW without
    // uninstalling (see features/offline/swBridge.ts setSwPassthrough).
    sw: {
      bypass: () => setSwPassthrough(true),
      intercept: () => setSwPassthrough(false),
    },
  };

  let MainComponent: React.ReactElement;

  // Load demo environment in development, main app in production
  if (import.meta.env.DEV && window.location.pathname.startsWith('/demos')) {
    MainComponent = <DemoApp />;
  } else {
    MainComponent = <Root />;
  }

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <ThemeProvider>
        <IntlProvider locale="en" defaultLocale="en">
          <QueryClientProvider client={queryClient}>
            <HolidayProvider>
              {MainComponent}
              <Toaster />
              <ReactQueryDevtools />
              {/* <TanStackRouterDevtools router={router} /> */}
            </HolidayProvider>
          </QueryClientProvider>
        </IntlProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}

// Start the app
initializeApp().catch((error) => logger.error('Failed to mount app', error));
